import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MessageInput = ({ onSendMessage, familyMembers }) => {
  const [message, setMessage] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);

  const handleInputChange = (e) => {
    const value = e?.target?.value;
    const cursorPos = e?.target?.selectionStart;
    
    setMessage(value);
    setCursorPosition(cursorPos);

    // Check for @ mention
    const textBeforeCursor = value?.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor?.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor?.substring(lastAtIndex + 1);
      // Check if there's a space after @, if so, don't show mentions
      if (!textAfterAt?.includes(' ')) {
        setMentionSearch(textAfterAt?.toLowerCase());
        setShowMentions(true);
        return;
      }
    }
    
    setShowMentions(false);
  };

  const handleMentionSelect = (member) => {
    const textBeforeCursor = message?.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor?.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const beforeMention = message?.substring(0, lastAtIndex);
      const afterCursor = message?.substring(cursorPosition);
      const newMessage = `${beforeMention}@${member?.name} ${afterCursor}`;
      
      setMessage(newMessage);
      setShowMentions(false);
      setMentionSearch('');
      
      // Focus back on textarea
      setTimeout(() => {
        textareaRef?.current?.focus();
        const newCursorPos = lastAtIndex + member?.name?.length + 2;
        textareaRef?.current?.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  const handleSend = () => {
    if (!message?.trim()) return;
    
    // Extract mentions
    const mentions = [];
    const mentionRegex = /@(\w+)/g;
    let match;
    
    while ((match = mentionRegex?.exec(message)) !== null) {
      const mentionedName = match?.[1];
      const member = familyMembers?.find(m => 
        m?.name?.toLowerCase() === mentionedName?.toLowerCase()
      );
      if (member) {
        mentions?.push(member?.id);
      }
    }
    
    onSendMessage(message, mentions);
    setMessage('');
    setShowMentions(false);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  const filteredMembers = familyMembers?.filter(member =>
    member?.name?.toLowerCase()?.includes(mentionSearch)
  );

  return (
    <div className="border-t border-border p-4 relative">
      {/* Mention suggestions */}
      {showMentions && filteredMembers?.length > 0 && (
        <div className="absolute bottom-full left-4 right-4 mb-2 bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto z-10">
          {filteredMembers?.map((member) => (
            <button
              key={member?.id}
              onClick={() => handleMentionSelect(member)}
              className="w-full flex items-center gap-3 p-3 hover:bg-accent transition-colors text-left"
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                style={{ backgroundColor: member?.color }}
              >
                {member?.initials}
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className="font-semibold text-sm"
                  style={{ color: member?.color }}
                >
                  {member?.name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {member?.role}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Input area */}
      <div className="flex gap-2">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Type a message... (use @ to mention family members)"
          className="flex-1 px-3 py-2 border border-input rounded-md bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring"
          rows={2}
        />
        <Button
          onClick={handleSend}
          disabled={!message?.trim()}
          iconName="send"
          size="icon"
          className="self-end"
          title="Send message"
        >
        </Button>
      </div>

      <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <Icon name="at-sign" size={12} />
          <span>Type @ to mention members</span>
        </div>
        <div className="flex items-center gap-1">
          <Icon name="corner-down-left" size={12} />
          <span>Enter to send, Shift+Enter for new line</span>
        </div>
      </div>
    </div>
  );
};

export default MessageInput;